// El recorrido de la carga en la ficha, con el diseño que pasó Víctor: seis
// fases (pedido, reserva, picking, paletizado, documentación, expedición), cada
// una con su dato debajo. Hecho = círculo verde con ✓; a medias = ámbar; en
// curso = naranja; pendiente = número en gris.
//
// Cada fase mira SU dato (`pasosFicha`), no el orden de los nueve estados: una
// carga en preparación parcial enseña la reserva a medias en ámbar, no en verde.
// Los nueve estados siguen en el chip de la cabecera.
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { CargaSalida, FaseFicha, SituacionPaso } from '@/types'
import { fechaDe, lineasPalets, pickingPct, reservaDe } from '@/utils/cargasSalida'
import { pasosFicha, transporteSinPreparar } from '@/utils/fichaCarga'
import { fmtDate, fmtFechaHora, fmtQty } from '../formatoSalida'

interface Props {
  c: CargaSalida
  dark: boolean
  /** Facturas de la carga: la documentación está hecha cuando hay alguna. */
  facturas: number
  /** `null` si el generador no está disponible para la empresa de la carga. */
  onPacking: (() => void) | null
  onFacturas: () => void
}

const MARCA: Record<SituacionPaso, { light: string; dark: string }> = {
  hecho:     { light: 'bg-emerald-600 text-white',             dark: 'bg-emerald-500 text-white' },
  parcial:   { light: 'bg-amber-500 text-white',               dark: 'bg-amber-500 text-white' },
  actual:    { light: 'bg-orange-600 text-white',              dark: 'bg-orange-500 text-white' },
  pendiente: { light: 'ring-1 ring-slate-300 text-slate-500',  dark: 'ring-1 ring-white/20 text-slate-400' },
}

export function StepperFases({ c, dark, facturas, onPacking, onFacturas }: Props) {
  const { t } = useTranslation()
  const pasos = pasosFicha(c, facturas)
  const sub = `text-[12px] tabular-nums ${dark ? 'text-slate-400' : 'text-slate-600'}`
  const rojo = dark ? 'text-rose-400' : 'text-rose-600'
  const ambar = dark ? 'text-amber-300' : 'text-amber-700'

  const chip = (texto: string, opts: { ok?: boolean; onClick?: (() => void) | null; proximamente?: boolean }) => {
    const base = 'text-[11px] font-semibold px-2 py-0.5 rounded-md border whitespace-nowrap'
    if (opts.proximamente || !opts.onClick) {
      return (
        <span
          aria-disabled="true"
          title={opts.proximamente ? t('cargasSalida.ficha.documentos.proximamente') : undefined}
          className={`${base} cursor-not-allowed opacity-50 ${dark ? 'border-white/10 text-slate-400' : 'border-slate-200 text-slate-500'}`}
        >
          {texto}
        </span>
      )
    }
    return (
      <button
        type="button"
        onClick={opts.onClick}
        className={`${base} cursor-pointer transition-colors ${
          opts.ok
            ? (dark ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-300' : 'border-emerald-300 bg-emerald-50 text-emerald-700')
            : (dark ? 'border-white/15 text-slate-200 hover:bg-white/8' : 'border-slate-300 text-slate-700 hover:bg-slate-100')
        }`}
      >
        {texto}{opts.ok ? ' ✓' : ''}
      </button>
    )
  }

  function cuerpo(fase: FaseFicha, s: SituacionPaso): { titulo: string; detalle: ReactNode } {
    switch (fase) {
      case 'pedido': {
        const f = fechaDe(c.fechaEnvioPrevista)
        return {
          titulo: t('cargasSalida.ficha.fases.pedido'),
          detalle: <p className={sub}>
            {f ? t('cargasSalida.ficha.fases.servicio', { fecha: fmtDate(c.fechaEnvioPrevista ?? undefined) })
               : t('cargasSalida.ficha.fases.sinFechaServicio')}
          </p>,
        }
      }
      case 'reserva': {
        const r = reservaDe(c)
        return {
          titulo: t(`cargasSalida.ficha.fases.reserva.${s === 'hecho' ? 'hecho' : s === 'parcial' ? 'parcial' : 'pendiente'}`),
          detalle: c.qtySolicitada > 0 && (
            <p className={sub}>
              {t('cargasSalida.ficha.fases.unidades', { hecho: fmtQty(c.qtyReservada), total: fmtQty(c.qtySolicitada) })}
              {s === 'parcial' && (
                <span className={`font-semibold ${ambar}`}> · {t('cargasSalida.ficha.fases.faltan', { n: fmtQty(r.falta) })}</span>
              )}
            </p>
          ),
        }
      }
      case 'picking': {
        const pct = Math.round(pickingPct(c))
        return {
          titulo: t(`cargasSalida.ficha.fases.picking.${s === 'hecho' ? 'hecho' : s === 'actual' ? 'actual' : 'pendiente'}`),
          detalle: s === 'pendiente'
            ? <p className={sub}>{t('cargasSalida.ficha.fases.pendiente')}</p>
            : (
              <div className="flex flex-col gap-1 pt-0.5">
                {s === 'actual' && (
                  <div className={`h-1.5 w-full rounded-full overflow-hidden ${dark ? 'bg-white/10' : 'bg-slate-200'}`}>
                    <div className={`h-full rounded-full ${dark ? 'bg-orange-500' : 'bg-orange-600'}`} style={{ width: `${pct}%` }} />
                  </div>
                )}
                <p className={sub}>
                  {t('cargasSalida.ficha.fases.unidades', { hecho: fmtQty(c.qtyPicking), total: fmtQty(c.qtySolicitada) })}
                  {s === 'actual' && ` · ${pct} %`}
                </p>
              </div>
            ),
        }
      }
      case 'paletizado': {
        const lineas = lineasPalets(c)
        const real = lineas.find(l => l.clave === 'real')
        const est = lineas.filter(l => l.clave !== 'real')
          .map(l => t(`cargasSalida.ficha.fases.est.${l.clave}`, { n: l.valor }))
        const partes: string[] = []
        if (c.cargaSinPalet === true) partes.push(t('cargasSalida.ficha.fases.sinPalet'))
        else if (real) partes.push(t('cargasSalida.ficha.fases.paletsReales', { count: real.valor }))
        else partes.push(t('cargasSalida.ficha.fases.pendiente'))
        if (est.length > 0) partes.push(t('cargasSalida.ficha.fases.estimados', { lista: est.join(' / ') }))
        return { titulo: t('cargasSalida.ficha.fases.paletizado'), detalle: <p className={sub}>{partes.join(' · ')}</p> }
      }
      case 'documentacion':
        return {
          titulo: t('cargasSalida.ficha.fases.documentacion'),
          detalle: (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {chip(t('cargasSalida.ficha.fases.packing'), { onClick: onPacking })}
              {chip(t('cargasSalida.ficha.documentos.albaran'), { proximamente: true })}
              {chip(t('cargasSalida.ficha.fases.factura'), { ok: facturas > 0, onClick: onFacturas })}
              {chip(t('cargasSalida.ficha.documentos.cmr'), { proximamente: true })}
            </div>
          ),
        }
      case 'expedicion': {
        if (s === 'hecho') {
          return { titulo: t('cargasSalida.ficha.fases.expedicion'), detalle: <p className={sub}>{t('cargasSalida.ficha.fases.cargada')}</p> }
        }
        const muelle = c.muelle
          ? <span>{t('cargasSalida.ficha.fases.muelle', { muelle: c.muelle })}{c.camion ? ` · ${t('cargasSalida.ficha.fases.camion', { camion: c.camion })}` : ''}</span>
          : <span className={rojo}>{t('cargasSalida.ficha.fases.sinMuelle')}</span>
        const transporte = c.estado === 'transporte_confirmado'
          ? <span>{t('cargasSalida.ficha.fases.transporteConfirmado', { fecha: fmtFechaHora(c.fechaTransporteConfirmada) })}</span>
          : c.estado === 'transporte'
            ? <span className={ambar}>{t('cargasSalida.ficha.fases.transporteMarcado')}</span>
            : <span className={rojo}>{t('cargasSalida.ficha.fases.sinTransporte')}</span>
        return {
          titulo: t('cargasSalida.ficha.fases.expedicion'),
          detalle: (
            <>
              <p className={sub}>{muelle} · {transporte}</p>
              {transporteSinPreparar(c) && (
                <p className={`text-[11px] font-semibold ${ambar}`}>
                  {t('cargasSalida.estadoCarga.transporteSinPreparar', { pct: Math.round(pickingPct(c)) })}
                </p>
              )}
            </>
          ),
        }
      }
    }
  }

  return (
    <ol className="flex flex-col" aria-label={t('cargasSalida.ficha.fases.titulo')}>
      {pasos.map((p, i) => {
        const ultimo = i === pasos.length - 1
        const { titulo, detalle } = cuerpo(p.fase, p.situacion)
        const fuerte = p.situacion !== 'pendiente'
        return (
          <li key={p.fase} className="flex gap-3" aria-current={p.situacion === 'actual' ? 'step' : undefined}>
            <div className="flex flex-col items-center">
              <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
                dark ? MARCA[p.situacion].dark : MARCA[p.situacion].light
              }`}>
                {p.situacion === 'hecho' ? (
                  <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4.5 10.5l3.5 3.5 7.5-8" />
                  </svg>
                ) : i + 1}
              </span>
              {!ultimo && <span className={`w-px flex-1 min-h-[10px] my-0.5 ${dark ? 'bg-white/10' : 'bg-slate-200'}`} />}
            </div>
            <div className="min-w-0 flex-1 pb-3">
              <p className={`text-[13px] leading-6 ${
                fuerte ? `font-semibold ${dark ? 'text-slate-100' : 'text-slate-900'}` : (dark ? 'text-slate-400' : 'text-slate-600')
              }`}>
                {titulo}
              </p>
              {detalle}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
